import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactMapboxGl, { Marker, Popup } from "react-mapbox-gl";
import { Button } from "antd";
import _ from "lodash";
import { DEFAULT_CENTER } from "configs/constants";

const Map = ReactMapboxGl({
  accessToken: process.env.REACT_APP_MAPBOX_KEY,
  onStyleLoad: (m) => m.resize(),
  zoom: 5
});

const MapImportPreview = ({
  routes,
  title = "Preview",
  mapHeight = 400,
  isLocation = false,
  mapStyle = {},
  zoom = [5]
}) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [bounds, setBounds] = useState([]);

  const mapRef = useRef(null);

  const openPopup = useCallback((index) => setSelectedIndex(index), []);
  const updateMapCenter = useCallback((coords) => setCenter(coords), []);

  // Get bounds dynamically
  const getBounds = useCallback((mapRoute) => {
    if (!mapRoute || mapRoute.length === 0) {
      setBounds([]);
      return;
    }

    // Filter routes with valid coordinates
    const validRoutes = mapRoute.filter(
      (r) =>
        r?.address?.lngLat?.length === 2 &&
        !isNaN(r.address.lngLat[0]) &&
        !isNaN(r.address.lngLat[1])
    );

    if (validRoutes.length === 0) {
      setBounds([]);
      return;
    }

    const maxLat = _.maxBy(validRoutes, (r) => r.address.lngLat[1])?.address.lngLat[1];
    const minLat = _.minBy(validRoutes, (r) => r.address.lngLat[1])?.address.lngLat[1];
    const maxLng = _.maxBy(validRoutes, (r) => r.address.lngLat[0])?.address.lngLat[0];
    const minLng = _.minBy(validRoutes, (r) => r.address.lngLat[0])?.address.lngLat[0];

    // Only set bounds if we have valid coordinates
    if (
      maxLat !== undefined &&
      minLat !== undefined &&
      maxLng !== undefined &&
      minLng !== undefined
    ) {
      setBounds([
        [minLng, minLat],
        [maxLng, maxLat]
      ]);
    }
  }, []);

  const handleMarkerOnClick = useCallback((markerIndex, coords) => {
    openPopup(markerIndex);
    updateMapCenter(coords);
  }, []);

  // Fit bounds on map style load
  const handleStyleLoad = useCallback(
    (map) => {
      mapRef.current = map;
      if (bounds && bounds.length === 2) {
        map.fitBounds(bounds, { padding: 50 });
      }
    },
    [bounds]
  );

  useEffect(() => {
    if (routes && routes.length > 0) {
      getBounds(routes); // Get new bounds whenever routes change

      // Also set initial center based on first route with valid coordinates
      const firstValidRoute = routes.find(
        (route) =>
          route?.address?.lngLat?.length === 2 &&
          !isNaN(route.address.lngLat[0]) &&
          !isNaN(route.address.lngLat[1])
      );
      if (
        firstValidRoute &&
        center[0] === DEFAULT_CENTER[0] &&
        center[1] === DEFAULT_CENTER[1]
      ) {
        setCenter(firstValidRoute.address.lngLat);
      }
    }
  }, [routes, center]);

  return (
    <div className="">
      {title && <h2 className="title p-1">{title}</h2>}
      <Map
        style="mapbox://styles/mapbox/streets-v11"
        center={center}
        containerStyle={{
          ...mapStyle,
          height: mapHeight,
          width: "100%",
          padding: 3
        }}
        onStyleLoad={handleStyleLoad}
        fitBounds={bounds.length === 2 ? bounds : undefined}
        fitBoundsOptions={{ padding: 50 }}
        movingMethod="easeTo"
        zoom={zoom}
      >
        {routes.map((route, index) => {
          if (!route?.address?.lngLat?.length) return null;
          const [lng, lat] = route.address.lngLat;

          const pinNum = index + 1;
          const pinNumLeftPos = pinNum > 9 ? "12px" : "16px";

          return (
            <React.Fragment key={index}>
              {selectedIndex === index && (
                <Popup coordinates={[lng, lat]} closeButton={true} closeOnClick={true}>
                  {route?.address?.address}
                </Popup>
              )}
              {!isLocation ? (
                <Marker
                  coordinates={[lng, lat]}
                  key={index}
                  onClick={() => handleMarkerOnClick(index, [lng, lat])}
                >
                  <img src="/icons/location.png" width="20" height="20" />
                </Marker>
              ) : (
                <Marker coordinates={[lng, lat]} key={index}>
                  {route?.title === "Pickup Location" || route?.type === "Pickup" ? (
                    <div style={{ position: "relative" }}>
                      <span
                        style={{
                          position: "absolute",
                          top: "4px",
                          left: pinNumLeftPos,
                          color: "#fff"
                        }}
                      >
                        {route?.position}
                      </span>
                      <img src="/icons/map-pinpoint-blue.svg" width="40" height="40" />
                    </div>
                  ) : (
                    <div style={{ position: "relative" }}>
                      <span
                        style={{
                          position: "absolute",
                          top: "4px",
                          left: pinNumLeftPos,
                          color: "#fff"
                        }}
                      >
                        {route?.position}
                      </span>
                      <img src="/icons/map-pinpoint-orange.svg" width="40" height="40" />
                    </div>
                  )}
                </Marker>
              )}
            </React.Fragment>
          );
        })}
        <Button
          onClick={() => {
            getBounds(routes);
            if (mapRef.current && bounds.length === 2) {
              mapRef.current.fitBounds(bounds, { padding: 50 });
            }
          }}
        >
          Show All
        </Button>
      </Map>
    </div>
  );
};

export default MapImportPreview;
